import React from "react";
import { Menu, Sun, Moon, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import { LocationSwitcher } from "./LocationSwitcher";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";

export function Header({ title, subtitle, actions, lastUpdatedMs, polling = false }) {
  const { theme, toggleTheme } = useTheme();
  const { company } = useAuth();

  const lastUpdatedLabel = lastUpdatedMs ? timeAgo(lastUpdatedMs) : null;

  return (
    <header
      className="sticky top-0 z-30 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/65 border-b border-border"
      data-testid="app-header"
    >
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
        {/* Mobile sidebar trigger */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            <Sidebar />
          </SheetContent>
        </Sheet>

        <div className="min-w-0 flex-1">
          {title && (
            <h1 className="font-heading text-lg sm:text-xl font-semibold tracking-tight truncate" data-testid="page-title">
              {title}
            </h1>
          )}
          {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
        </div>

        <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground" data-testid="last-updated-indicator">
          {polling ? (
            <span className="flex items-center gap-1.5">
              <span className="operon-spinner" /> Updating…
            </span>
          ) : lastUpdatedLabel ? (
            <span>Updated {lastUpdatedLabel}</span>
          ) : null}
        </div>

        <LocationSwitcher />

        {actions}

        <Button variant="ghost" size="icon" onClick={toggleTheme} title="Toggle theme" data-testid="theme-toggle">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>
    </header>
  );
}

function timeAgo(ms) {
  const diff = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
